import { useActorRef, useSelector } from '@xstate/react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Link } from 'react-router';
import type { ActorRefFrom, SnapshotFrom } from 'xstate';
import { UserContext } from '~/app';
import { Button } from '~/components';
import type { ProviderServiceListingSearchItem } from '~/features/employer/provider-search/schemas';
import { selectUnmaskViewerScope } from '~/features/shared/market-content/ResponseUnmaskPanel';
import { buildServiceOpportunitiesUrl } from '~/routing/urls';
import {
  serviceResponseSessionMachine,
  type ServiceResponseActors,
} from './serviceResponseSession.machine';

const Context = createContext<{
  viewerScopeKey: string;
  actor: ActorRefFrom<typeof serviceResponseSessionMachine>;
  registerDiscovery: (listingId: number) => () => void;
} | null>(null);
type Session = SnapshotFrom<typeof serviceResponseSessionMachine>;
const selectResponses = (s: Session) => s.context.responses;
const selectDiscoveryBegins = (s: Session) => s.context.discoveryBegins;
type BeginSnapshot = ReturnType<ServiceResponseActors['begin']['getSnapshot']>;
const selectDiscoveryBusy = (s: BeginSnapshot | undefined) =>
  s !== undefined && (!s.matches('idle') || s.context.confirmedResponseId !== null);
const selectDiscoveryResetRevision = (s: BeginSnapshot | undefined) =>
  s?.context.discoveryResetRevision ?? 0;
const selectDiscoveryPending = (s: BeginSnapshot) =>
  s.matches('sending') || s.matches('recovering');
const selectDiscoveryReview = (s: BeginSnapshot) => s.matches('discoveryReview');
const selectConfirmedResponse = (s: BeginSnapshot) => s.context.confirmedResponseId;
const selectOptionalConfirmedResponse = (s: BeginSnapshot | undefined) =>
  s?.context.confirmedResponseId ?? null;
const selectRecoveredResponse = (s: BeginSnapshot) => s.context.recoveredResponseId;
const selectDiscoveryError = (s: BeginSnapshot) => s.context.error;
const selectDiscoveryUncertain = (s: BeginSnapshot) => s.context.uncertain;

const DiscoveryRecoveryNotice = ({
  listingId,
  actor,
  inline = false,
}: {
  listingId: number;
  actor: ServiceResponseActors['begin'];
  inline?: boolean;
}) => {
  const pending = useSelector(actor, selectDiscoveryPending);
  const review = useSelector(actor, selectDiscoveryReview);
  const confirmed = useSelector(actor, selectConfirmedResponse);
  const recovered = useSelector(actor, selectRecoveredResponse);
  const error = useSelector(actor, selectDiscoveryError);
  const uncertain = useSelector(actor, selectDiscoveryUncertain);
  // The success receipt belongs to the search row; the layout copy never surfaces it.
  const showConfirmed = inline && confirmed !== null;
  if (!pending && !review && !showConfirmed) return null;
  return (
    <section
      className={inline ? 'mt-3 space-y-2' : 'm-4 space-y-2'}
      aria-label={`Service negotiation recovery for listing ${listingId}`}
    >
      {!inline && <p className="text-sm">Service negotiation for listing {listingId}</p>}
      {pending && <p role="status">Checking negotiation outcome…</p>}
      {/* [MCV-1951] The search row already offers "Open provider response" once the response exists */}
      {showConfirmed && (
        <p role="status">
          Negotiation started successfully. Response #{confirmed} is ready to review.
        </p>
      )}
      {review && (
        <>
          <p role="alert">{error}</p>
          {recovered !== null && (
            <Link className="text-primary underline" to={buildServiceOpportunitiesUrl(recovered)}>
              Review recovered service response
            </Link>
          )}
          {uncertain && (
            <p className="text-sm">
              Finding a response does not confirm that the original selection started.
            </p>
          )}
          <p className="text-sm">
            Discarding clears this saved attempt and recovery tracking. It does not cancel or undo
            server work, including availability holds.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              wrap
              label="Refresh negotiation outcome"
              onClick={() => actor.send({ type: 'REFRESH' })}
            />
            {uncertain && (
              <Button
                type="button"
                wrap
                label="Retry original selection"
                onClick={() => actor.send({ type: 'RETRY' })}
              />
            )}
            <Button
              type="button"
              wrap
              colorVariant="outline"
              label="Discard local recovery"
              onClick={() => actor.send({ type: 'DISCARD' })}
            />
          </div>
        </>
      )}
    </section>
  );
};
const selectConversationNotice = (
  s: ReturnType<ServiceResponseActors['conversation']['getSnapshot']>
) => {
  if (s.context.visible) return null;
  if (s.context.uncertain) return 'A service response action has an unconfirmed outcome.';
  if (
    s.matches('sending') ||
    s.matches('withdrawing') ||
    s.matches('rejecting') ||
    s.matches('accepting') ||
    s.matches('countering') ||
    (s.matches('loading') && s.context.pending)
  )
    return 'A service response action is still being checked.';
  if (s.context.readError && s.context.confirmedAction)
    return 'A service response action succeeded, but its refreshed details are unavailable.';
  return null;
};
const selectBeginNotice = (s: ReturnType<ServiceResponseActors['begin']['getSnapshot']>) =>
  s.matches('uncertain')
    ? 'A service negotiation has an unconfirmed outcome.'
    : s.matches('sending') || s.matches('recovering')
      ? 'A service negotiation is still being checked.'
      : null;

const RecoveryNotice = ({
  responseId,
  actors,
}: {
  responseId: number;
  actors: ServiceResponseActors;
}) => {
  const conversation = useSelector(actors.conversation, selectConversationNotice);
  const begin = useSelector(actors.begin, selectBeginNotice);
  if (!conversation && !begin) return null;
  return (
    <div className="m-4 space-y-2">
      <p role="status">{[conversation, begin].filter(Boolean).join(' ')}</p>
      <Link className="text-primary underline" to={buildServiceOpportunitiesUrl(responseId)}>
        Review service response {responseId}
      </Link>
    </div>
  );
};

const Owner = ({
  children,
  viewerScopeKey,
  recoveryLinks,
}: {
  children: ReactNode;
  viewerScopeKey: string;
  recoveryLinks: boolean;
}) => {
  const actor = useActorRef(serviceResponseSessionMachine);
  const responses = useSelector(actor, selectResponses);
  const discoveryBegins = useSelector(actor, selectDiscoveryBegins);
  const [visibleDiscovery, setVisibleDiscovery] = useState<ReadonlySet<number>>(new Set());
  const registerDiscovery = useCallback((listingId: number) => {
    setVisibleDiscovery((current) => new Set(current).add(listingId));
    return () =>
      setVisibleDiscovery((current) => {
        const next = new Set(current);
        next.delete(listingId);
        return next;
      });
  }, []);
  const contextValue = useMemo(
    () => ({ viewerScopeKey, actor, registerDiscovery }),
    [viewerScopeKey, actor, registerDiscovery]
  );
  return (
    <Context.Provider value={contextValue}>
      {recoveryLinks &&
        Object.entries(responses).map(([id, actors]) => (
          <RecoveryNotice key={id} responseId={Number(id)} actors={actors} />
        ))}
      {recoveryLinks &&
        Object.entries(discoveryBegins)
          .filter(([id]) => !visibleDiscovery.has(Number(id)))
          .map(([id, begin]) => (
            <DiscoveryRecoveryNotice key={id} listingId={Number(id)} actor={begin} />
          ))}
      {children}
    </Context.Provider>
  );
};

/** The layout owns same-viewer operations; standalone views get an isolated boundary. */
export const ServiceResponseBoundary = ({
  children,
  viewerScopeKey,
  recoveryLinks = false,
}: {
  children: ReactNode;
  viewerScopeKey: string;
  recoveryLinks?: boolean;
}) => {
  const parent = useContext(Context);
  return parent?.viewerScopeKey === viewerScopeKey ? (
    children
  ) : (
    <Owner key={viewerScopeKey} viewerScopeKey={viewerScopeKey} recoveryLinks={recoveryLinks}>
      {children}
    </Owner>
  );
};

export const ServiceWorkspaceBoundary = ({ children }: { children: ReactNode }) => {
  const viewer = useSelector(UserContext.useActorRef(), selectUnmaskViewerScope);
  return (
    <ServiceResponseBoundary viewerScopeKey={viewer} recoveryLinks>
      {children}
    </ServiceResponseBoundary>
  );
};

export const useServiceResponseActors = (responseId: number) => {
  const owner = useContext(Context);
  if (!owner) throw new Error('Service responses require their viewer boundary.');
  const actor = owner.actor;
  const response = useSelector(actor, (s) => s.context.responses[responseId]);
  useEffect(() => {
    actor.send({ type: 'OPEN_RESPONSE', responseId });
  }, [actor, responseId]);
  return response;
};

type DiscoveryControls = {
  busy: boolean;
  confirmedResponseId: number | null;
  notice: ReactNode;
  begin: () => void;
};
type DiscoveryProps = {
  listing: ProviderServiceListingSearchItem;
  selectedKeys: string[];
  onResetSelection: () => void;
  onConfirmed: (responseId: number) => void;
  children: (controls: DiscoveryControls) => ReactNode;
};
export const ServiceDiscoveryBeginFlow = (props: DiscoveryProps) => {
  const owner = useContext(Context);
  if (!owner) throw new Error('Service discovery requires its viewer boundary.');
  const listingId = props.listing.providerServiceListingId;
  const actor = owner.actor;
  const begin = useSelector(actor, (s) => s.context.discoveryBegins[listingId]);
  const registerDiscovery = owner.registerDiscovery;
  useEffect(() => registerDiscovery(listingId), [registerDiscovery, listingId]);
  return (
    <OwnedDiscoveryBegin
      actor={begin}
      onResetSelection={props.onResetSelection}
      onConfirmed={props.onConfirmed}
      listingId={listingId}
      existingResponseId={props.listing.existingResponseId ?? null}
      onBegin={() =>
        actor.send({
          type: 'BEGIN_DISCOVERY',
          listing: props.listing,
          selectedKeys: props.selectedKeys,
        })
      }
    >
      {props.children}
    </OwnedDiscoveryBegin>
  );
};
const OwnedDiscoveryBegin = ({
  actor,
  onBegin,
  onResetSelection,
  onConfirmed,
  listingId,
  existingResponseId,
  children,
}: {
  actor: ServiceResponseActors['begin'] | undefined;
  onBegin: () => void;
  onResetSelection: () => void;
  onConfirmed: (responseId: number) => void;
  listingId: number;
  existingResponseId: number | null;
  children: DiscoveryProps['children'];
}) => {
  const busy = useSelector(actor, selectDiscoveryBusy);
  const confirmedResponseId = useSelector(actor, selectOptionalConfirmedResponse);
  // A retained receipt must not redirect someone returning to search via Back.
  const observedConfirmation = useRef(confirmedResponseId);
  useEffect(() => {
    if (observedConfirmation.current === confirmedResponseId) return;
    observedConfirmation.current = confirmedResponseId;
    if (confirmedResponseId !== null) onConfirmed(confirmedResponseId);
  }, [confirmedResponseId, onConfirmed]);
  // [MCV-1951] A confirmation this row mounted with was kept from before (another visit, or the
  // rows reloading). The freshly loaded row is the truth for it: search reports every live
  // response as existingResponseId, so none means it turned terminal, and a kept confirmation
  // would otherwise block a new Begin (the receipt no longer has a Dismiss to clear it).
  const carriedOver = useRef(confirmedResponseId !== null);
  useEffect(() => {
    if (!carriedOver.current || confirmedResponseId === null || existingResponseId !== null) return;
    // Only the kept one: a negotiation begun on this row afterwards keeps its receipt.
    carriedOver.current = false;
    actor?.send({ type: 'CLEAR_CONFIRMATION' });
  }, [actor, confirmedResponseId, existingResponseId]);
  const resetRevision = useSelector(actor, selectDiscoveryResetRevision);
  const observedReset = useRef(resetRevision);
  useEffect(() => {
    if (resetRevision === observedReset.current) return;
    observedReset.current = resetRevision;
    onResetSelection();
  }, [resetRevision, onResetSelection]);
  return children({
    busy,
    confirmedResponseId,
    notice: actor ? <DiscoveryRecoveryNotice listingId={listingId} actor={actor} inline /> : null,
    begin: onBegin,
  });
};
